import { useId, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { formatMoney } from '../format';
import type { PaymentRecordItem } from '../types';

const NINE_DIGITS = /\d{9,}/;
export const VOID_REASON_MIN = 3;
export const VOID_REASON_MAX = 500;

/**
 * Void a record: a confirmation with a required, length-bounded reason. The record is kept (never deleted), stops counting toward YTD and the 1099 total, and
 * can never re-enter a payment flow. A void does not reverse funds or recall a check, and a voided check number is not reused.
 */
export function VoidDialog({ record, onClose, onConfirm }: {
  record: Pick<PaymentRecordItem, 'paymentId' | 'contractorName' | 'amount' | 'status'>;
  onClose: () => void;
  /** Resolves to an error message, or null when the record was voided (the page then closes this dialog). */
  onConfirm: (reason: string) => Promise<string | null>;
}) {
  const uid = useId();
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const text = reason.trim();
    if (text.length < VOID_REASON_MIN) return setFieldError(`Enter a reason of at least ${VOID_REASON_MIN} characters.`);
    if (text.length > VOID_REASON_MAX) return setFieldError(`The reason must be ${VOID_REASON_MAX} characters or fewer.`);
    if (NINE_DIGITS.test(text)) return setFieldError('Don’t enter account or routing numbers in the reason.');
    setFieldError(null);
    setBusy(true);
    setError(null);
    const refusal = await onConfirm(text);
    if (refusal) {
      setError(refusal);
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Void payment record"
      size="sm"
      className="cp-sheet"
      role="alertdialog"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn cp-btn--danger" onClick={() => void submit()} disabled={busy}>{busy ? 'Voiding…' : 'Void payment'}</button>
        </>
      )}
    >
      <p>
        Void the {formatMoney(record.amount)} payment to <strong>{record.contractorName}</strong> ({record.paymentId}), currently <strong>{record.status}</strong>?
      </p>
      <ul className="cp-void-notes">
        <li>The record is kept for the audit trail and marked Voided. It no longer counts toward year-to-date or 1099 totals.</li>
        <li>This does not reverse any funds or recall a check, and the check number can’t be reused.</li>
        <li>A voided record can’t be changed, paid or voided again.</li>
      </ul>
      <div className="jr-field">
        <label htmlFor={`${uid}-reason`}>Reason for voiding <span aria-hidden="true" className="jr-req">*</span></label>
        <textarea id={`${uid}-reason`} data-autofocus rows={3} maxLength={VOID_REASON_MAX} value={reason} aria-required="true" aria-invalid={fieldError ? true : undefined} onChange={(e) => setReason(e.target.value)} />
        <span className="cp-muted" aria-live="polite">{reason.trim().length} / {VOID_REASON_MAX}</span>
        {fieldError && <p className="jr-error" role="alert">{fieldError}</p>}
      </div>
      {error && <p className="jr-error" role="alert" data-testid="void-error">{error}</p>}
    </ModalDialog>
  );
}
